import {
  compterParFiltre,
  consigneCreation,
  consigneDansFiltre,
  consigneModification,
  ecrireConditionObjet,
  type FiltreConsignes,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  type Consigne,
  type ConsigneFournisseur,
  corps,
  ErreurApi,
  gere,
  lire,
  requeteMoi,
  requetes,
} from '../api'
import { LIBELLES_EXECUTANT_CONSIGNE, LIBELLES_STATUT_CONSIGNE, options } from '../libelles'
import { LigneConsigneFournisseur } from './ConsignesFournisseur'
import { type Champ, Formulaire } from './Formulaire'
import { Ligne, Section, Vide } from './Section'
import { Onglets } from './Ui'
import { confirmer } from './confirmation'

// Consignes de tri : la mémoire de l'assistant pour trier les mails. Écrites ici, elles
// sont actives ; proposées par l'assistant (MCP), elles attendent d'être activées ici. Les
// consignes de fournisseur (rattachement des pièces d'un fournisseur à plusieurs endroits) s'y
// relisent aussi, avec le même filtre : à revoir, à activer, toutes.

export type Evaluation = { erreurs: string[]; avertissements: string[] }
export type Essai = {
  mailsLus: number
  declenches: number
  changeraient: number
  note: string
  exemples: { mailId: string; objet: string | null; expediteur: string | null; change: boolean }[]
}
type Rattrapage = {
  mailsDeclenches: number
  appliquees: { tacheId: string; titre: string; changements: string[] }[]
  laissees: { tacheId: string; titre: string; raison: string }[]
}

export function useChoix() {
  const tiers = useQuery(requetes.tiers).data ?? []
  const regles = useQuery(requetes.regles).data ?? []
  const baux = useQuery(requetes.tousLesBaux).data ?? []
  const entites = useQuery(requetes.entites).data ?? []
  const nomTiers = (id: string | null) => tiers.find((t) => t.id === id)?.nom
  const nomEntite = (id: string | null) => entites.find((e) => e.id === id)?.nom
  return {
    tiers: tiers.map((t) => ({ valeur: t.id, libelle: t.nom })),
    regles: regles.map((r) => ({ valeur: r.id, libelle: `${r.entite} · ${r.libelle}` })),
    baux: baux.map((b) => ({
      valeur: b.id,
      libelle: `${nomEntite(b.entiteId) ?? ''} · bail de ${nomTiers(b.locataireId) ?? 'locataire inconnu'}${b.gestionnaireId ? `, géré par ${nomTiers(b.gestionnaireId)}` : ''}`,
    })),
  }
}

export function champsConsigne(
  choix: ReturnType<typeof useChoix>,
  creation: { entites: { valeur: string; libelle: string }[]; transverse: boolean } | null,
): Champ[] {
  return [
    ...(creation
      ? ([
          {
            nom: 'entiteId',
            libelle: 'Entité',
            type: 'choix',
            requis: !creation.transverse,
            options: creation.entites,
            aide: creation.transverse
              ? 'Vide : consigne transverse, qui ne fait que guider le tri (administrateurs).'
              : undefined,
          },
        ] satisfies Champ[])
      : []),
    {
      nom: 'sujet',
      libelle: 'Sujet',
      type: 'texte',
      requis: true,
      aide: '2 à 5 mots, style télégraphique.',
    },
    {
      nom: 'quand',
      libelle: 'Quand l’appliquer',
      type: 'zone',
      lignes: 2,
      requis: true,
      aide: 'Télégraphique, 15 mots au plus (relu à chaque tri) : organisme, objet, demande. Exemple : notification CA, documents banque assurance en ligne.',
    },
    {
      nom: 'expediteurs',
      libelle: 'Expéditeurs',
      type: 'liste',
      aide: 'Un par ligne : adresse ou domaine. Reconnu sans modèle.',
    },
    {
      nom: 'objets',
      libelle: 'Objet du mail',
      type: 'liste',
      aide: 'Une condition par ligne, reconnue sans modèle : est, contient, commence par, finit par, ne contient pas, puis « : » et le texte (commence par : Vos documents). Sans casse ni accents. Avec des expéditeurs, les deux doivent correspondre.',
    },
    {
      nom: 'ignorer',
      libelle: 'Ignorer ces mails (ni tâche ni pièce, sans appel à Claude)',
      type: 'booleen',
    },
    {
      nom: 'mailVautPiece',
      libelle:
        'Le mail vaut pièce : reçu sans pièce jointe, son corps rendu en PDF entre comme pièce (expéditeur exigé)',
      type: 'booleen',
    },
    {
      nom: 'texte',
      libelle: 'Précisions',
      type: 'zone',
      lignes: 3,
      aide: 'Facultatif, 400 caractères : lues seulement quand la consigne est retenue (ce qui en relève, ce qui n’en relève pas).',
    },
    { nom: 'regleId', libelle: 'Règle d’échéance', type: 'choix', options: choix.regles },
    { nom: 'bailId', libelle: 'Bail', type: 'choix', options: choix.baux },
    {
      nom: 'executant',
      libelle: 'Qui exécute',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_EXECUTANT_CONSIGNE),
    },
    { nom: 'executantTiersId', libelle: 'Tiers désigné', type: 'choix', options: choix.tiers },
    { nom: 'attenteTiers', libelle: 'Mettre la tâche en attente du tiers', type: 'booleen' },
    {
      nom: 'delaiJours',
      libelle: 'Délai (jours)',
      type: 'entier',
      aide: 'Date due quand le mail n’en donne pas.',
    },
  ]
}

export function Remarques({ e }: { e: Evaluation | null }) {
  if (!e || (e.erreurs.length === 0 && e.avertissements.length === 0)) return null
  return (
    <div className="bloc">
      {e.erreurs.map((x) => (
        <p key={x} className="alerte">
          {x}
        </p>
      ))}
      {e.avertissements.map((x) => (
        <p key={x} className="discret">
          ⚠ {x}
        </p>
      ))}
    </div>
  )
}

function resume(c: Consigne): string {
  if (c.ignorer) return 'mail ignoré, ni tâche ni pièce'
  return [
    c.mailVautPiece && 'le mail vaut pièce (rendu en PDF)',
    c.regle && `échéance « ${c.regle} »`,
    c.executant === 'gestionnaire' && 'confiée au gestionnaire du bail',
    c.executant === 'tiers' && `confiée à ${c.executantTiers ?? 'un tiers'}`,
    c.attenteTiers && 'en attente du tiers',
    c.delaiJours && `sous ${c.delaiJours} jours`,
  ]
    .filter(Boolean)
    .join(', ')
}

export function LigneConsigne({ c, modifiable }: { c: Consigne; modifiable: boolean }) {
  const client = useQueryClient()
  const choix = useChoix()
  const [essai, setEssai] = useState<{ evaluation: Evaluation; essai: Essai } | null>(null)
  const [rattrapage, setRattrapage] = useState<Rattrapage | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const rafraichir = () => client.invalidateQueries({ queryKey: ['consignes'] })
  // Le tri ne l'applique qu'aux mails à venir : ses déclencheurs rejoués sur les tâches ouvertes.
  const rattraper = async () => {
    if (
      !(await confirmer(
        c.ignorer
          ? 'Fermer les demandes ouvertes nées des mails que cette consigne ignore ?'
          : 'Appliquer cette consigne aux tâches ouvertes nées des mails qu’elle déclenche (entité, pièces, exécutant ; fermées s’il n’y a rien à faire) ?',
      ))
    )
      return
    try {
      setErreur(null)
      setRattrapage(await lire(api.consignes[':id'].appliquer.$post({ param: { id: c.id } })))
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Application impossible.')
    }
  }
  const agir = async (action: 'activer' | 'desactiver') => {
    try {
      setErreur(null)
      await lire(api.consignes[':id'].action.$post({ param: { id: c.id }, json: { action } }))
      await rafraichir()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }
  const essayer = async () => {
    try {
      setErreur(null)
      setEssai(
        await lire(
          api.consignes.verifier.$post({
            json: corps(consigneCreation, {
              entiteId: c.entiteId,
              sujet: c.sujet,
              quand: c.quand,
              texte: c.texte,
              expediteurs: c.expediteurs,
              objets: c.objets,
              ignorer: c.ignorer,
              mailVautPiece: c.mailVautPiece,
              regleId: c.regleId,
              bailId: c.bailId,
              executant: c.executant,
              executantTiersId: c.executantTiersId,
              attenteTiers: c.attenteTiers,
              delaiJours: c.delaiJours,
            }),
          }),
        ),
      )
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Essai impossible.')
    }
  }
  return (
    <Ligne
      edition={
        modifiable
          ? (fermer) => (
              <Formulaire
                champs={champsConsigne(choix, null)}
                initial={{ ...c, objets: c.objets.map(ecrireConditionObjet) }}
                onAnnuler={fermer}
                onValider={async (v) => {
                  await lire(
                    api.consignes[':id'].$patch({
                      param: { id: c.id },
                      json: corps(consigneModification, v),
                    }),
                  )
                  await rafraichir()
                  fermer()
                }}
              />
            )
          : undefined
      }
      onSupprimer={
        modifiable && c.utilisations === 0
          ? async () => {
              await lire(api.consignes[':id'].$delete({ param: { id: c.id } }))
              await rafraichir()
            }
          : undefined
      }
    >
      <strong>{c.sujet}</strong> <span className="badge">{LIBELLES_STATUT_CONSIGNE[c.statut]}</span>
      {c.viaAssistant && <span className="badge">Proposée par l’assistant</span>}
      <div className="discret">Quand : {c.quand}</div>
      {c.expediteurs.length > 0 && (
        <div className="discret">Expéditeurs : {c.expediteurs.join(', ')}</div>
      )}
      {c.objets.length > 0 && (
        <div className="discret">Objet : {c.objets.map(ecrireConditionObjet).join(' ; ')}</div>
      )}
      {c.texte && <div className="discret">Précisions : {c.texte}</div>}
      {resume(c) && <div className="discret">Effet : {resume(c)}</div>}
      <div className="discret">
        {c.utilisations === 0
          ? 'Pas encore utilisée.'
          : `Utilisée ${c.utilisations} fois, la dernière le ${new Date(c.utiliseeLe!).toLocaleDateString('fr-FR')}.`}
      </div>
      {modifiable && (
        <div className="boutons">
          {c.statut !== 'active' && (
            <button type="button" className="bouton" onClick={() => void agir('activer')}>
              Activer
            </button>
          )}
          {c.statut !== 'inactive' && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() => void agir('desactiver')}
            >
              Désactiver
            </button>
          )}
          <button type="button" className="bouton-discret" onClick={() => void essayer()}>
            Essai à blanc
          </button>
          {c.statut === 'active' && (c.expediteurs.length > 0 || c.objets.length > 0) && (
            <button type="button" className="bouton-discret" onClick={() => void rattraper()}>
              Appliquer aux tâches ouvertes
            </button>
          )}
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {rattrapage && (
        <div className="bloc">
          <p className="discret">
            {rattrapage.mailsDeclenches} mail(s) déclenché(s) ;{' '}
            {rattrapage.appliquees.length === 0
              ? 'aucune tâche ouverte à changer.'
              : `${rattrapage.appliquees.length} tâche(s) changée(s).`}
          </p>
          {rattrapage.appliquees.map((t) => (
            <div key={t.tacheId} className="discret">
              ✓{' '}
              <Link to="/taches/$id" params={{ id: t.tacheId }}>
                {t.titre}
              </Link>{' '}
              : {t.changements.join(', ')}
            </div>
          ))}
          {rattrapage.laissees.map((t) => (
            <div key={t.tacheId} className="discret">
              ✗{' '}
              <Link to="/taches/$id" params={{ id: t.tacheId }}>
                {t.titre}
              </Link>{' '}
              : {t.raison}
            </div>
          ))}
        </div>
      )}
      {essai && (
        <div className="bloc">
          <Remarques e={essai.evaluation} />
          <p className="discret">
            {essai.essai.declenches} mail(s) sur {essai.essai.mailsLus} lus en 90 jours
            {essai.essai.changeraient > 0 &&
              (c.ignorer
                ? `, qui seraient ignorés`
                : `, dont ${essai.essai.changeraient} classé(s) aujourd’hui sur une autre entité`)}
            . {essai.essai.note}
          </p>
          {essai.essai.exemples.map((m) => (
            <div key={m.mailId} className="discret">
              {m.change ? '↻ ' : '✓ '}
              {m.objet ?? 'Sans objet'} ({m.expediteur})
            </div>
          ))}
        </div>
      )}
    </Ligne>
  )
}

export function SectionConsignes() {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const consignes = useQuery(requetes.consignes)
  const entites = useQuery(requetes.entites).data ?? []
  const choix = useChoix()
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const gerees = entites
    .filter((e) => !e.archivee && gere(moi, e.id))
    .map((e) => ({ valeur: e.id, libelle: e.nom }))
  const modifiable = (c: Consigne) =>
    c.entiteId === null ? moi.administrateur : gerees.some((e) => e.valeur === c.entiteId)
  const fournisseur = useQuery(requetes.consignesFournisseur)
  const [choisi, setChoisi] = useState<FiltreConsignes | null>(null)
  const maintenant = new Date()
  const toutes = [...(consignes.data ?? []), ...(fournisseur.data ?? [])]
  const comptes = compterParFiltre(toutes, maintenant)
  // Ce qui attend une activation s'ouvre d'abord : sinon, on ne le verrait jamais.
  const filtre: FiltreConsignes = choisi ?? (comptes.a_activer > 0 ? 'a_activer' : 'toutes')
  const garder = (c: Consigne | ConsigneFournisseur) => consigneDansFiltre(filtre, c, maintenant)
  const parEntite = new Map<string, Consigne[]>()
  for (const c of (consignes.data ?? []).filter(garder))
    parEntite.set(c.entite ?? 'Toutes entités', [
      ...(parEntite.get(c.entite ?? 'Toutes entités') ?? []),
      c,
    ])
  const parTiers = new Map<string, ConsigneFournisseur[]>()
  for (const c of (fournisseur.data ?? []).filter(garder))
    parTiers.set(c.tiers, [...(parTiers.get(c.tiers) ?? []), c])

  return (
    <Section
      titre="Consignes de tri"
      libelleAjout="Nouvelle consigne"
      ajout={
        gerees.length > 0 || moi.administrateur
          ? (fermer) => (
              <Formulaire
                champs={champsConsigne(choix, {
                  entites: gerees,
                  transverse: moi.administrateur,
                })}
                initial={{
                  executant: 'responsables',
                  attenteTiers: false,
                  ignorer: false,
                  mailVautPiece: false,
                }}
                onAnnuler={fermer}
                onValider={async (v) => {
                  const r = await lire(api.consignes.$post({ json: corps(consigneCreation, v) }))
                  setEvaluation(r.evaluation)
                  await client.invalidateQueries({ queryKey: ['consignes'] })
                  fermer()
                }}
              />
            )
          : undefined
      }
    >
      <p className="discret">
        La mémoire du tri des mails : quand un mail relève d’un sujet, sur quelle entité il va, à
        quelle échéance il se rattache, qui l’exécute, ou s’il faut l’ignorer. Expéditeur et objet
        se reconnaissent sans modèle ; « quand » est lu par Claude, en style télégraphique. Une
        consigne ne fait que classer, affecter ou écarter ; payer, déclarer et signer restent
        humains. L’assistant peut en proposer (connecteur MCP, méthode « qualifier_consigne ») :
        elles s’activent ici.
      </p>
      {comptes.a_activer > 0 && (
        <p className="alerte">
          {comptes.a_activer} consigne(s) proposée(s) par l’assistant : à relire puis activer.
        </p>
      )}
      <Onglets
        valeur={filtre}
        onChange={setChoisi}
        onglets={[
          { valeur: 'a_revoir', libelle: 'À revoir', compte: comptes.a_revoir },
          { valeur: 'a_activer', libelle: 'À activer', compte: comptes.a_activer },
          { valeur: 'toutes', libelle: 'Toutes', compte: comptes.toutes },
        ]}
      />
      {filtre === 'a_revoir' && (
        <p className="discret">
          Désactivées, ou actives sans avoir jamais servi un mois après leur activation.
        </p>
      )}
      <Remarques e={evaluation} />
      {(consignes.isPending || fournisseur.isPending) && <Vide>Chargement…</Vide>}
      {!consignes.isPending && !fournisseur.isPending && parEntite.size + parTiers.size === 0 && (
        <Vide>Aucune consigne ici.</Vide>
      )}
      {[...parEntite.entries()].map(([entite, liste]) => (
        <div key={entite} className="bloc">
          <h3>{entite}</h3>
          {liste.map((c) => (
            <LigneConsigne key={c.id} c={c} modifiable={modifiable(c)} />
          ))}
        </div>
      ))}
      {parTiers.size > 0 && (
        <div className="bloc">
          <h3>Consignes de fournisseur</h3>
          <p className="discret">
            Pour un fournisseur qui dessert plusieurs endroits (énergie, eau, télécom) : une
            référence lue sur sa pièce (PRM, contrat, adresse du point de livraison) mène à une
            entité. Elles se créent aussi sur la fiche du tiers.
          </p>
          {[...parTiers.entries()].map(([tiers, liste]) => (
            <div key={tiers}>
              {liste.map((c) => (
                <LigneConsigneFournisseur key={c.id} c={c} gerees={gerees} avecTiers />
              ))}
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}
